import { supabase } from '../../lib/supabase';
import type { CRMContact, ContactAccount, ContactEmail, ContactPhone } from '../../types/crm';
import type { CRMState } from './types';
import { failed, oneRow } from './writeResult';
import type { Database, Json } from '../../types/supabase';

type Set = (partial: Partial<CRMState> | ((state: CRMState) => Partial<CRMState>)) => void;
type Get = () => CRMState;

type ContactRow = Database['public']['Tables']['crm_contacts']['Row'];
type ContactEmailRow = Database['public']['Tables']['contact_emails']['Row'];
type ContactPhoneRow = Database['public']['Tables']['contact_phones']['Row'];
type ContactAccountRow = Database['public']['Tables']['contact_accounts']['Row'];

/** The contact's current primary mail, phone or account loses that rank (its failure stops the gesture). */
async function demotePrimary(table: 'contact_emails' | 'contact_phones' | 'contact_accounts', contactId: string): Promise<void> {
  const { error } = await supabase.from(table).update({ is_primary: false }).eq('contact_id', contactId).eq('is_primary', true);
  if (error) throw new Error(error.message);
}

/** Normalize a `contact_emails` DB row: nullable label/flag/timestamp → defaults. */
function toContactEmail(row: ContactEmailRow): ContactEmail {
  return {
    id: row.id,
    contact_id: row.contact_id,
    email: row.email,
    label: row.label ?? '',
    is_primary: row.is_primary ?? false,
    created_at: row.created_at ?? '',
  };
}

/** Normalize a `contact_phones` DB row: nullable label/flag/timestamp → defaults. */
function toContactPhone(row: ContactPhoneRow): ContactPhone {
  return {
    id: row.id,
    contact_id: row.contact_id,
    phone: row.phone,
    label: row.label ?? '',
    is_primary: row.is_primary ?? false,
    created_at: row.created_at ?? '',
  };
}

/**
 * Normalize a `contact_accounts` DB row into the domain `ContactAccount`.
 * The optional joined `account` is a reduced {id, name} projection for display.
 */
function toContactAccount(
  row: ContactAccountRow & { account?: { id: string; name: string } | null },
): ContactAccount {
  return {
    id: row.id,
    contact_id: row.contact_id,
    account_id: row.account_id,
    is_primary: row.is_primary ?? false,
    role: row.role,
    created_at: row.created_at ?? '',
    // Reduced projection bridged to the full-account domain type; see toCRMContact.
    account: (row.account ?? undefined) as ContactAccount['account'],
  };
}

// The contacts query joins the multi-value relations onto each contact row.
// The `account` embedded in each link is a reduced projection (id + name only,
// enough for the sidebar label), not a full CRMAccount — mirror that here so the
// row shape matches what PostgREST actually returns.
type JoinedContactAccount = Omit<ContactAccount, 'account'> & {
  account?: { id: string; name: string } | null;
};
type ContactRowJoined = ContactRow & {
  emails?: CRMContact['emails'];
  phones?: CRMContact['phones'];
  contact_accounts?: JoinedContactAccount[];
};

/** Narrow a nullable custom_fields JSON into the domain Record shape. */
function toRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize a joined `crm_contacts` DB row into the domain `CRMContact`.
 * The DB stores `status` as free-form text and `custom_fields` as nullable
 * `Json`; narrow both, and default the nullable notification flag to false.
 */
function toCRMContact(row: ContactRowJoined): CRMContact {
  return {
    id: row.id,
    account_id: row.account_id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    title: row.title,
    status: row.status === 'inactive' ? 'inactive' : 'active',
    owner_id: row.owner_id,
    last_contacted_at: row.last_contacted_at,
    feature_notifications_enabled: row.feature_notifications_enabled ?? false,
    custom_fields: toRecord(row.custom_fields),
    created_at: row.created_at,
    updated_at: row.updated_at,
    emails: row.emails,
    phones: row.phones,
    // The joined `account` is a reduced {id, name} projection for the sidebar
    // label, not a full CRMAccount. ContactAccount.account is typed as the full
    // account, so bridge it here at the fetch boundary. Proper fix: model the
    // reduced projection on ContactAccount.account in src/types/crm.ts.
    contact_accounts: row.contact_accounts as ContactAccount[] | undefined,
  };
}

/** What the contacts page reads of a contact: its main account, numbers, mails and every account it belongs to. */
const CONTACT_SELECT =
  '*, account:crm_accounts(id, name), emails:contact_emails(*), phones:contact_phones(*), contact_accounts(*, account:crm_accounts(id, name))';

/**
 * One contact as the screens read it (its main account, mails, phones, linked accounts), saying
 * whether it is missing or unreadable — a sheet tells « introuvable » from « lecture impossible ».
 */
export async function readContact(id: string): Promise<{ contact: CRMContact | null; error: string | null }> {
  const { data, error } = await supabase.from('crm_contacts').select(CONTACT_SELECT).eq('id', id).maybeSingle();
  if (error) return { contact: null, error: error.message };
  return { contact: data ? toCRMContact(data as ContactRowJoined) : null, error: null };
}

export const createContactsSlice = (set: Set, get: Get) => ({
  contacts: [] as CRMContact[],

  fetchContacts: async (accountId?: string) => {
    try {
      let query = supabase
        .from('crm_contacts')
        .select(CONTACT_SELECT)
        .order('created_at', { ascending: false });

      if (accountId) {
        query = query.eq('account_id', accountId);
      }

      const { data, error } = await query;
      if (error) throw error;
      set({ contacts: ((data ?? []) as ContactRowJoined[]).map(toCRMContact) });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch contacts';
      set({ error: message });
    }
  },

  /**
   * One contact, as the list reads it: a contact the list does not hold (PostgREST stops it at 1000
   * rows, without error) can still be opened by its link. Null when it does not exist (or is
   * unreadable: the error is kept in `error`, as for the list).
   */
  fetchContact: async (id: string): Promise<CRMContact | null> => {
    const { contact, error } = await readContact(id);
    if (error) set({ error });
    return contact;
  },

  createContact: async (data: Partial<CRMContact>) => {
    set({ loading: true });
    try {
      const { data: newContact, error } = await supabase
        .from('crm_contacts')
        .insert({
          account_id: data.account_id,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email,
          phone: data.phone,
          title: data.title,
          status: data.status || 'active',
        })
        .select()
        .single();

      if (error) throw error;

      // Insert into multi-value tables
      if (data.email) {
        await supabase.from('contact_emails').insert({
          contact_id: newContact.id, email: data.email, label: 'pro', is_primary: true,
        });
      }
      if (data.phone) {
        await supabase.from('contact_phones').insert({
          contact_id: newContact.id, phone: data.phone, label: 'mobile', is_primary: true,
        });
      }
      if (data.account_id) {
        await supabase.from('contact_accounts').insert({
          contact_id: newContact.id, account_id: data.account_id, is_primary: true,
        });
      }

      await get().fetchContacts();
      set({ loading: false });
      return toCRMContact(newContact);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create contact';
      set({ error: message, loading: false });
      return null;
    }
  },

  updateContact: async (id: string, data: Partial<CRMContact>) => {
    set({ loading: true });
    try {
      // Strip relation-only fields and normalize custom_fields to the DB Json type.
      const { emails, phones, contact_accounts, custom_fields, ...rest } = data;
      void emails; void phones; void contact_accounts;
      const payload: Database['public']['Tables']['crm_contacts']['Update'] = {
        ...rest,
        ...(custom_fields !== undefined ? { custom_fields: custom_fields as Json } : {}),
        updated_at: new Date().toISOString(),
      };
      await oneRow(supabase.from('crm_contacts').update(payload).eq('id', id).select('id'), 'Le contact');
    } catch (error: unknown) {
      return failed(set, error, 'Failed to update contact', { loading: false });
    }
    await get().fetchContacts();
    set({ loading: false });
    return null;
  },

  deleteContact: async (id: string) => {
    try {
      const { error } = await supabase
        .from('crm_contacts')
        .delete()
        .eq('id', id);

      if (error) {
        return { success: false, error: error.message };
      }

      await get().fetchContacts();
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete contact';
      return { success: false, error: message };
    }
  },

  // === Contact multi-value CRUD ===
  // Each says its OWN failure (returned, and kept in `error` as before): the list read again after
  // it is never blamed on the write (relecture du 13/10/2026: a saved change showed « not saved »).

  addContactEmail: async (contactId: string, email: string, label: string, isPrimary: boolean) => {
    let added: ContactEmail;
    try {
      if (isPrimary) await demotePrimary('contact_emails', contactId);
      const { data, error } = await supabase
        .from('contact_emails')
        .insert({ contact_id: contactId, email, label, is_primary: isPrimary })
        .select()
        .single();
      if (error) throw error;
      added = toContactEmail(data);
    } catch (error: unknown) {
      failed(set, error, 'Erreur ajout email');
      return null;
    }
    await get().fetchContacts();
    return added;
  },

  updateContactEmail: async (emailId: string, data: { email?: string; label?: string }) => {
    try {
      await oneRow(supabase.from('contact_emails').update(data).eq('id', emailId).select('id'), "L'email");
    } catch (error: unknown) {
      return failed(set, error, 'Erreur mise a jour email');
    }
    await get().fetchContacts();
    return null;
  },

  removeContactEmail: async (emailId: string) => {
    try {
      await oneRow(supabase.from('contact_emails').delete().eq('id', emailId).select('id'), "L'email");
    } catch (error: unknown) {
      return failed(set, error, 'Erreur suppression email');
    }
    await get().fetchContacts();
    return null;
  },

  setPrimaryEmail: async (contactId: string, emailId: string) => {
    try {
      await demotePrimary('contact_emails', contactId);
      await oneRow(supabase.from('contact_emails').update({ is_primary: true }).eq('id', emailId).select('id'), "L'email");
    } catch (error: unknown) {
      return failed(set, error, 'Erreur mise a jour email principal');
    }
    await get().fetchContacts();
    return null;
  },

  addContactPhone: async (contactId: string, phone: string, label: string, isPrimary: boolean) => {
    let added: ContactPhone;
    try {
      if (isPrimary) await demotePrimary('contact_phones', contactId);
      const { data, error } = await supabase
        .from('contact_phones')
        .insert({ contact_id: contactId, phone, label, is_primary: isPrimary })
        .select()
        .single();
      if (error) throw error;
      added = toContactPhone(data);
    } catch (error: unknown) {
      failed(set, error, 'Erreur ajout telephone');
      return null;
    }
    await get().fetchContacts();
    return added;
  },

  updateContactPhone: async (phoneId: string, data: { phone?: string; label?: string }) => {
    try {
      await oneRow(supabase.from('contact_phones').update(data).eq('id', phoneId).select('id'), 'Le téléphone');
    } catch (error: unknown) {
      return failed(set, error, 'Erreur mise a jour telephone');
    }
    await get().fetchContacts();
    return null;
  },

  removeContactPhone: async (phoneId: string) => {
    try {
      await oneRow(supabase.from('contact_phones').delete().eq('id', phoneId).select('id'), 'Le téléphone');
    } catch (error: unknown) {
      return failed(set, error, 'Erreur suppression telephone');
    }
    await get().fetchContacts();
    return null;
  },

  setPrimaryPhone: async (contactId: string, phoneId: string) => {
    try {
      await demotePrimary('contact_phones', contactId);
      await oneRow(supabase.from('contact_phones').update({ is_primary: true }).eq('id', phoneId).select('id'), 'Le téléphone');
    } catch (error: unknown) {
      return failed(set, error, 'Erreur mise a jour telephone principal');
    }
    await get().fetchContacts();
    return null;
  },

  addContactAccount: async (contactId: string, accountId: string, isPrimary: boolean, role?: string) => {
    let added: ContactAccount;
    try {
      if (isPrimary) await demotePrimary('contact_accounts', contactId);
      const { data, error } = await supabase
        .from('contact_accounts')
        .insert({ contact_id: contactId, account_id: accountId, is_primary: isPrimary, role: role || null })
        .select('*, account:crm_accounts(id, name)')
        .single();
      if (error) throw error;
      added = toContactAccount(data);
    } catch (error: unknown) {
      failed(set, error, 'Erreur ajout compte');
      return null;
    }
    await get().fetchContacts();
    return added;
  },

  removeContactAccount: async (linkId: string) => {
    try {
      await oneRow(supabase.from('contact_accounts').delete().eq('id', linkId).select('id'), 'Le lien au compte');
    } catch (error: unknown) {
      return failed(set, error, 'Erreur suppression lien compte');
    }
    await get().fetchContacts();
    return null;
  },

  setPrimaryAccount: async (contactId: string, linkId: string) => {
    try {
      await demotePrimary('contact_accounts', contactId);
      await oneRow(supabase.from('contact_accounts').update({ is_primary: true }).eq('id', linkId).select('id'), 'Le lien au compte');
    } catch (error: unknown) {
      return failed(set, error, 'Erreur mise a jour compte principal');
    }
    await get().fetchContacts();
    return null;
  },
});
